import React, { useId } from 'react';
import { useToast } from '@chakra-ui/react';
import '../CSS/Global.css';
import {
  IMAGE_ACCEPT_ATTR,
  IMAGE_TYPES_HELPER_TEXT,
  filterValidImageFiles,
} from '../utils/imageUploadRules';

function fileKey(f) {
  return `${f.name}|${f.size}|${f.lastModified}`;
}

/**
 * Listing photos — parent owns `files` so state survives leaving step 4 and remounting this component.
 * @param {{ files: File[], onFilesChange: import('react').Dispatch<import('react').SetStateAction<File[]>> }} props
 */
const ImageUpload = ({ files = [], onFilesChange }) => {
  const inputId = useId();
  const toast = useToast();

  const appendFiles = async (rawFiles) => {
    const list = Array.from(rawFiles || []);
    if (!list.length) return;

    const accepted = await filterValidImageFiles(list, (msg) => {
      toast({
        title: 'Image not accepted',
        description: msg,
        status: 'error',
        duration: 6000,
        isClosable: true,
        position: 'top',
      });
    });

    if (!accepted.length) {
      toast({
        title: 'No images added',
        description:
          'None of the selected files met the rules (JPG, JPEG, PNG, or WebP; max 1 MB each).',
        status: 'warning',
        duration: 8000,
        isClosable: true,
        position: 'top',
      });
      return;
    }

    onFilesChange((prev) => {
      const prevArr = Array.isArray(prev) ? prev : [];
      const seen = new Set(prevArr.map(fileKey));
      const merged = [...prevArr];
      for (const f of accepted) {
        const k = fileKey(f);
        if (!seen.has(k)) {
          seen.add(k);
          merged.push(f);
        }
      }
      return merged;
    });
  };

  const handleImageUpload = (e) => {
    // Copy files before clearing the input; some browsers can clear FileList too.
    const picked = Array.from(e.target.files || []);
    e.target.value = '';
    void appendFiles(picked);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    void appendFiles(e.dataTransfer.files);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const list = Array.isArray(files) ? files : [];

  return (
    <div className="image-upload-container">
      <p className="image-upload-hint" style={{ fontSize: '0.9rem', color: '#555', marginBottom: 12 }}>
        {IMAGE_TYPES_HELPER_TEXT}
      </p>
      {list.length > 0 && (
        <p style={{ fontSize: '0.85rem', color: '#2d3748', marginBottom: 8 }}>
          {list.length} photo{list.length === 1 ? '' : 's'} will be uploaded with your listing.
        </p>
      )}
      <div
        className="drop-zone"
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        style={{ border: '2px dashed #ccc', padding: '20px', minHeight: '150px' }}
      >
        <div className="upload-icon">
          <i className="fas fa-cloud-upload-alt"></i>
        </div>
        <label htmlFor={inputId} className="browse-button">
          Upload
        </label>
        <input
          type="file"
          id={inputId}
          multiple
          accept={IMAGE_ACCEPT_ATTR}
          onChange={handleImageUpload}
          style={{ display: 'none' }}
        />
      </div>

      <div className="preview">
        {list.length > 0 && <h4>Selected images</h4>}
        <div className="image-preview-grid">
          {list.map((image, index) => (
            <img
              key={`${fileKey(image)}-${index}`}
              src={URL.createObjectURL(image)}
              alt={`Uploaded ${index + 1}`}
              className="image-preview"
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default ImageUpload;
